import React, { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Header,
  Divider,
  Form,
  Input,
  Button,
  Segment,
  Table,
  Icon,
  Message,
  Modal,
  List
} from "semantic-ui-react";
import TextareaAutosize from "react-textarea-autosize";
import AddressSearchModal from "../../../common/components/AddressSearchModal/AddressSearchModal";
import { formatPrice, formatDate, decodeEntities } from "../../../common/utils/format";

const ADDRESS_OPTIONS = [
  { key: "office", text: "회사", value: "office" },
  { key: "home", text: "집 (회원정보 주소)", value: "home" },
  { key: "recent", text: "최근 배송지", value: "recent" },
  { key: "etc", text: "기타 (직접 입력)", value: "etc" }
];

const bookTypeLabel = entry => {
  if (entry.bookType === "전자책") {
    return `전자책 - ${entry.ebookStore}`;
  }
  return "일반도서";
};

const reqTypeLabel = code => (code === "01" ? "개인" : "공용");

// 비고 빠른 입력 — 사내 convention "YY년 M월 도서신청" / "YY년 M-1,M월 도서신청".
// 1월일 때만 전달이 작년 12월이라 두 연도 모두 표시 ("25년 12월, 26년 1월 도서신청").
const thisMonthEtc = () => {
  const d = new Date();
  return `${String(d.getFullYear()).slice(-2)}년 ${d.getMonth() + 1}월 도서신청`;
};
const lastAndThisMonthEtc = () => {
  const d = new Date();
  const curY = d.getFullYear();
  const curM = d.getMonth() + 1;
  const prevY = curM === 1 ? curY - 1 : curY;
  const prevM = curM === 1 ? 12 : curM - 1;
  const curYY = String(curY).slice(-2);
  const prevYY = String(prevY).slice(-2);
  return prevY === curY
    ? `${curYY}년 ${prevM},${curM}월 도서신청`
    : `${prevYY}년 ${prevM}월, ${curYY}년 ${curM}월 도서신청`;
};


const BookMultiRequest = ({
  draft,
  books,
  addressType,
  addressEtc,
  myAddress,
  recentAddress,

  onChangeDraft,
  onSearchBook,
  searchHint,
  onAddBook,
  onResetDraft,

  onRemoveBook,

  // 보관함 (제목만 저장 — 신청 시 재검색). 베타 활성 사용자에게만 노출.
  cartEnabled,
  onKeepDraft,
  onKeepBook,
  keepHint,

  // 보관함에서 불러오기 (신청에 사용)
  carts,
  onUseCart,
  onDeleteCart,
  confirmUseName,
  onConfirmUse,
  onCancelUse,

  onChangeAddressType,
  onChangeAddressEtc,

  onSubmit,
  onCancel,
  isSubmitting,
  submitError,

  // 좌측(데스크탑) / 모달(모바일) 에 표시할 신청 현황 패널
  statsPanel
}) => {
  // 모바일 토글 모달
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  // 주소 검색 모달 (기타 직접입력용)
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  // 보관함 불러오기 접이식 토글
  const [cartOpen, setCartOpen] = useState(false);
  const addressEtcRef = useRef(null);
  const totalPrice = useMemo(
    () => books.reduce((s, b) => s + (Number(b.price) || 0), 0),
    [books]
  );
  const totalCount = books.length;

  const resolvedAddress = useMemo(() => {
    switch (addressType) {
      case "office":
        return "회사";
      case "home":
        return myAddress || "(회원정보에 주소를 먼저 등록해주세요)";
      case "recent":
        return recentAddress || "(최근 배송지가 없습니다)";
      case "etc":
        return addressEtc;
      default:
        return "";
    }
  }, [addressType, addressEtc, myAddress, recentAddress]);

  const canAdd =
    draft.bookName.trim().length > 0 &&
    draft.reqUrl.trim().length > 0 &&
    Number(draft.price) > 0;

  return (
    <Container style={{ padding: "6em 0em" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
        {/* 좌측 신청 현황 (데스크탑만) */}
        {statsPanel ? (
          <div className="mr-stats-desktop" style={{ flex: "0 0 280px" }}>
            {statsPanel}
          </div>
        ) : null}

        {/* 우측 신청 폼 */}
        <div style={{ flex: "1 1 600px", minWidth: 0 }}>
      <Header as="h2">
        도서 신청
        {statsPanel ? (
          <Button
            size="tiny"
            basic
            className="mr-stats-mobile-btn"
            style={{ marginLeft: "1em", verticalAlign: "middle" }}
            onClick={() => setStatsModalOpen(true)}
          >
            📊 신청 현황
          </Button>
        ) : null}
      </Header>
      <Divider section />

      {/* 보관함에서 불러오기 — 제목만 저장돼 있어, 사용 시 재검색으로 현재 정보 선택.
          베타 활성 사용자에게만 노출 (비활성 시 carts 는 빈 배열). */}
      {cartEnabled && carts && carts.length > 0 ? (
        <Segment color="teal" style={{ marginBottom: "1.5em" }}>
          <Header
            as="h4"
            style={{ cursor: "pointer", marginBottom: 0 }}
            onClick={() => setCartOpen(prev => !prev)}
          >
            <Icon name={cartOpen ? "triangle down" : "triangle right"} />
            <Icon name="bookmark" />
            보관함에서 불러오기 ({carts.length})
          </Header>
          {cartOpen ? (
            <List divided relaxed style={{ marginTop: "0.8em" }}>
              {carts.map(c => (
                <List.Item key={c.cartSeq}>
                  <List.Content floated="right">
                    <Button
                      size="tiny"
                      primary
                      type="button"
                      onClick={() => onUseCart(c.bookName)}
                    >
                      불러오기
                    </Button>
                    <Button
                      size="tiny"
                      type="button"
                      onClick={() => onDeleteCart(c.cartSeq)}
                    >
                      삭제
                    </Button>
                  </List.Content>
                  <List.Content>
                    <div>{decodeEntities(c.bookName)}</div>
                    <div style={{ color: "#999", fontSize: "0.85em" }}>
                      {formatDate(c.regDt)} 보관
                    </div>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          ) : (
            <div style={{ color: "#999", fontSize: "0.85em", marginTop: "0.5em" }}>
              담아둔 도서를 펼쳐 신청에 사용할 수 있습니다. 링크·가격은 다시 검색해 최신 정보로 선택됩니다.
            </div>
          )}
        </Segment>
      ) : null}

      {/* 입력 폼 */}
      <Form>
            {/* 개인/공용은 신청구분 토글과 무관하게 항상 같은 행 마지막 위치 —
                분기별 중복 렌더링으로 위치가 점프하던 문제 fix */}
            <Form.Group widths="equal">
              <Form.Field required>
                <label>신청구분</label>
                <select
                  value={draft.bookType}
                  onChange={e => onChangeDraft("bookType", e.target.value)}
                >
                  <option value="일반도서">일반도서</option>
                  <option value="전자책">전자책</option>
                </select>
              </Form.Field>
              {draft.bookType === "전자책" ? (
                <Form.Field required>
                  <label>ebook 사이트</label>
                  <select
                    value={draft.ebookStore}
                    onChange={e =>
                      onChangeDraft("ebookStore", e.target.value)
                    }
                  >
                    <option value="교보문고">교보문고</option>
                    <option value="리디북스">리디북스</option>
                  </select>
                </Form.Field>
              ) : null}
              <Form.Field>
                <label>개인/공용</label>
                <select
                  value={draft.reqType}
                  onChange={e => onChangeDraft("reqType", e.target.value)}
                >
                  <option value="01">개인</option>
                  <option value="02">공용</option>
                </select>
              </Form.Field>
            </Form.Group>

            <Form.Field required>
              <label>도서명</label>
              <Input
                action={
                  <Button primary onClick={onSearchBook}>
                    검색
                  </Button>
                }
                placeholder="도서명"
                value={draft.bookName}
                onChange={e => onChangeDraft("bookName", e.target.value)}
              />
              {/* 검색어 미입력 힌트 (alert 대체) — 입력 시작하면 사라짐 */}
              {searchHint ? (
                <div style={{ marginTop: "0.3em", fontSize: "0.9em", color: "#db2828" }}>
                  {searchHint}
                </div>
              ) : null}
            </Form.Field>

            <Form.Group>
              <Form.Field width={11} required>
                <label>도서구매 URL</label>
                <Input
                  placeholder="https://..."
                  value={draft.reqUrl}
                  onChange={e => onChangeDraft("reqUrl", e.target.value)}
                />
              </Form.Field>
              <Form.Field width={5} required>
                <label>가격 (원)</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={draft.price}
                  onChange={e => onChangeDraft("price", e.target.value)}
                />
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <label>비고</label>
              <div style={{ marginBottom: "0.4em", display: "flex", gap: "0.4em", flexWrap: "wrap" }}>
                <Button
                  size="tiny"
                  basic
                  type="button"
                  onClick={() => onChangeDraft("etc", lastAndThisMonthEtc())}
                >
                  전달+이번달 도서신청
                </Button>
                <Button
                  size="tiny"
                  basic
                  type="button"
                  onClick={() => onChangeDraft("etc", thisMonthEtc())}
                >
                  이번달 도서신청
                </Button>
              </div>
              <TextareaAutosize
                minRows={2}
                maxRows={5}
                style={{ width: "100%", padding: "8px" }}
                placeholder="기타 메모"
                value={draft.etc}
                onChange={e => onChangeDraft("etc", e.target.value)}
              />
            </Form.Field>

            {/* 보관함 담기 완료 안내 — 2.5초 후 자동 소거 */}
            {keepHint ? (
              <div style={{ marginBottom: "0.4em", textAlign: "right", fontSize: "0.9em", color: "#21ba45" }}>
                <Icon name="check" />
                {keepHint} <Link to="/cart">보관함 보기</Link>
              </div>
            ) : null}
            <div style={{ textAlign: "right" }}>
              <Button type="button" onClick={onResetDraft}>
                초기화
              </Button>
              {/* 보관함은 제목만 저장 — URL/가격 없이도 담을 수 있음. 베타 활성 시만 노출 */}
              {cartEnabled ? (
                <Button
                  type="button"
                  disabled={!draft.bookName.trim()}
                  onClick={onKeepDraft}
                  title="다음에 신청할 도서로 제목만 보관"
                >
                  <Icon name="bookmark outline" />
                  보관함에 담기
                </Button>
              ) : null}
              <Button primary type="button" disabled={!canAdd} onClick={onAddBook}>
                <Icon name="plus" />
                추가하기
              </Button>
            </div>
          </Form>

      <Divider hidden />

      {/* 담은 도서 목록 */}
      {books.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
        <Table celled compact unstackable style={{ minWidth: 900 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>신청구분</Table.HeaderCell>
              <Table.HeaderCell>도서명</Table.HeaderCell>
              <Table.HeaderCell>도서구매 URL</Table.HeaderCell>
              <Table.HeaderCell>구분</Table.HeaderCell>
              <Table.HeaderCell>가격</Table.HeaderCell>
              <Table.HeaderCell>비고</Table.HeaderCell>
              <Table.HeaderCell textAlign="center" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {books.map((b, idx) => (
              <Table.Row key={idx}>
                <Table.Cell>{bookTypeLabel(b)}</Table.Cell>
                <Table.Cell>{decodeEntities(b.bookName)}</Table.Cell>
                <Table.Cell
                  style={{
                    wordBreak: "break-all",
                    maxWidth: 0
                  }}
                >
                  <a
                    href={b.reqUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {b.reqUrl}
                  </a>
                </Table.Cell>
                <Table.Cell>{reqTypeLabel(b.reqType)}</Table.Cell>
                <Table.Cell textAlign="right" style={{ whiteSpace: "nowrap" }}>
                  {formatPrice(b.price)}<span style={{ color: "#999", marginLeft: 2 }}>원</span>
                </Table.Cell>
                <Table.Cell
                  style={{
                    whiteSpace: "pre-wrap",
                    color: b.etc ? undefined : "#bbb"
                  }}
                >
                  {decodeEntities(b.etc) || "—"}
                </Table.Cell>
                <Table.Cell textAlign="center" collapsing>
                  {cartEnabled ? (
                    <Icon
                      name="bookmark outline"
                      link
                      color="grey"
                      size="large"
                      style={{ marginRight: "0.5em" }}
                      onClick={() => onKeepBook(idx)}
                      title="보관함으로 (제목만 보관, 다음에 신청)"
                    />
                  ) : null}
                  <Icon
                    name="close"
                    link
                    color="grey"
                    size="large"
                    onClick={() => onRemoveBook(idx)}
                    title="삭제"
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan="4" textAlign="right">
                총 {totalCount}권 · 합계
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right">
                <span style={{ color: "#2185d0" }}>
                  {formatPrice(totalPrice)}원
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell colSpan="2" />
            </Table.Row>
          </Table.Footer>
        </Table>
        </div>
      ) : (
        <Message>
          <p>위 폼에서 도서를 추가하면 이 곳에 목록이 누적됩니다.</p>
        </Message>
      )}

      {/* 배송지 (모든 도서 공통) */}
      <Segment>
        <Header as="h3">배송지</Header>
        <Form>
          <Form.Group inline>
            {ADDRESS_OPTIONS.map(opt => (
              <Form.Field key={opt.key}>
                <input
                  type="radio"
                  id={`addr-${opt.key}`}
                  name="addressType"
                  value={opt.value}
                  checked={addressType === opt.value}
                  onChange={() => onChangeAddressType(opt.value)}
                />
                <label
                  htmlFor={`addr-${opt.key}`}
                  style={{ marginLeft: 4, marginRight: 16 }}
                >
                  {opt.text}
                </label>
              </Form.Field>
            ))}
          </Form.Group>
          {/* 집 선택 시 회원정보 주소 미등록 안내 */}
          {addressType === "home" && !myAddress ? (
            <div style={{ marginBottom: "0.5em", fontSize: "0.9em", color: "#999" }}>
              회원정보 수정에서 집 주소를 먼저 등록해 주세요.{" "}
              <Link to="/updateUserInfo">회원정보 수정으로 이동</Link>
            </div>
          ) : null}
          {addressType === "etc" ? (
            <Form.Field>
              {/* 주소 검색(팝업)과 직접 입력을 분리해 각각 명확하게 —
                  검색 버튼이 Input 에 붙어 있으면 입력하면 검색되는 것처럼 오해됨 */}
              <div style={{ marginBottom: "0.5em" }}>
                <Button
                  type="button"
                  basic
                  primary
                  onClick={() => setAddressModalOpen(true)}
                >
                  <Icon name="search" />
                  주소 검색
                </Button>
                <span style={{ marginLeft: "0.5em", fontSize: "0.9em", color: "#999" }}>
                  또는 아래 칸에 직접 입력하세요
                </span>
              </div>
              <Input
                ref={addressEtcRef}
                placeholder="주소를 직접 입력 (주소 검색 후에는 상세주소를 이어서 입력)"
                value={addressEtc}
                onChange={e => onChangeAddressEtc(e.target.value)}
              />
            </Form.Field>
          ) : (
            <Message size="small">
              <span style={{ color: "#666" }}>{resolvedAddress}</span>
            </Message>
          )}
        </Form>
      </Segment>

      {/* 신청 실패 사유 (alert 대체) */}
      {submitError ? (
        <Message negative>
          <p>{submitError}</p>
        </Message>
      ) : null}

      {/* 액션 */}
      <div style={{ textAlign: "right" }}>
        <Button onClick={onCancel} disabled={isSubmitting}>취소</Button>
        <Button
          primary
          onClick={onSubmit}
          disabled={books.length === 0 || isSubmitting}
          loading={isSubmitting}
        >
          신청하기
        </Button>
      </div>
        </div>
      </div>

      {/* 모바일 모달 — 신청 현황 토글 */}
      {statsPanel ? (
        <Modal
          open={statsModalOpen}
          onClose={() => setStatsModalOpen(false)}
          closeIcon
          size="small"
        >
          <Modal.Header>내 신청 현황</Modal.Header>
          <Modal.Content>{statsPanel}</Modal.Content>
        </Modal>
      ) : null}

      {/* 주소 검색 — 기타 직접입력. 선택 후 상세주소 이어서 입력하도록 포커스 */}
      <AddressSearchModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        onSelect={addr => {
          onChangeAddressEtc(addr + " ");
          // 모달 닫힘 리렌더 후 포커스
          setTimeout(() => {
            if (addressEtcRef.current) addressEtcRef.current.focus();
          }, 0);
        }}
      />

      {/* 보관함 도서 불러오기 확인 — 입력 중이던 draft 가 사라짐을 안내 */}
      <Modal open={!!confirmUseName} onClose={onCancelUse} size="tiny">
        <Modal.Header>보관함 도서 불러오기</Modal.Header>
        <Modal.Content>
          <p>
            현재 입력 중인 도서 정보가 사라지고 <b>"{confirmUseName}"</b> (으)로 새로
            채웁니다.
          </p>
          <p style={{ color: "#666", fontSize: "0.95em" }}>
            신청구분(일반도서 / 전자책)을 고른 뒤 검색해서 신청하세요.
            계속하시겠습니까?
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onCancelUse}>취소</Button>
          <Button primary onClick={onConfirmUse}>
            계속
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 반응형 — 좁은 화면에선 데스크탑 좌측 패널 숨김, 헤더 옆 모바일 버튼 노출.
          Semantic UI Button 의 default display 가 specificity 더 강해서 !important 로 강제. */}
      <style>{`
        .mr-stats-mobile-btn { display: none !important; }
        @media (max-width: 900px) {
          .mr-stats-desktop { display: none !important; }
          .mr-stats-mobile-btn { display: inline-block !important; }
        }
      `}</style>
    </Container>
  );
};

export default BookMultiRequest;
